"use client";

import { Badge } from "@velpha/ui/components/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@velpha/ui/components/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@velpha/ui/components/chart";
import { useIsMobile } from "@velpha/ui/hooks/use-media-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@velpha/ui/components/table";
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from "recharts";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

type IndexedPerformanceRow = {
  month: string;
  deposit: number;
  bond: number;
  reit: number;
  equity: number;
  gold: number;
  commodity: number;
  bitcoin: number;
};

type LineKey = keyof Omit<IndexedPerformanceRow, "month">;
type Level = "low" | "medium" | "high";

const STRINGS: Record<
  Locale,
  {
    level: Record<Level, string>;
    product: Record<LineKey, string>;
    source: Record<LineKey, string>;
    chartTitle: string;
    chartDescription: string;
    monthLabel: (year: string, month: number) => string;
    columns: { product: string; return: string; risk: string; source: string };
  }
> = {
  ja: {
    level: { low: "低", medium: "中", high: "高" },
    product: {
      deposit: "預金",
      bond: "先進国債券",
      reit: "REIT",
      equity: "株式",
      gold: "金",
      commodity: "コモディティ",
      bitcoin: "仮想通貨",
    },
    source: {
      deposit: "金利の積み上げ",
      bond: "利息収入と景気悪化局面の下支え",
      reit: "賃料収入と不動産市況の回復",
      equity: "企業利益の成長と配当",
      gold: "通貨不安やインフレ、地政学リスク時の逃避需要",
      commodity: "資源価格の上昇とインフレ連動",
      bitcoin: "採用期待と需給主導の価格上昇",
    },
    chartTitle: "2021年4月末を100とした時系列",
    chartDescription:
      "2021年4月末から2026年3月末までの月末ベースです。預金は2026年3月時点の1年定期金利0.425%を一定とした参考線です。",
    monthLabel: (year, month) => `${year}年${month}月`,
    columns: { product: "商品", return: "リターン", risk: "リスク", source: "リターンの中心" },
  },
  en: {
    level: { low: "Low", medium: "Mid", high: "High" },
    product: {
      deposit: "Deposits",
      bond: "Developed-market bonds",
      reit: "REITs",
      equity: "Equities",
      gold: "Gold",
      commodity: "Commodities",
      bitcoin: "Crypto",
    },
    source: {
      deposit: "Interest accumulating over time",
      bond: "Coupon income, plus support when the economy weakens",
      reit: "Rental income and recovery in the property market",
      equity: "Growth in corporate earnings, plus dividends",
      gold: "Safe-haven demand during currency stress, inflation, and geopolitical risk",
      commodity: "Rising resource prices and a link to inflation",
      bitcoin: "Expectations of adoption and supply-demand driven price moves",
    },
    chartTitle: "Indexed to 100 at the end of April 2021",
    chartDescription:
      "Month-end values from the end of April 2021 to the end of March 2026. Deposits are a reference line holding the March 2026 one-year time-deposit rate of 0.425% constant.",
    monthLabel: (year, month) => `${year}-${String(month).padStart(2, "0")}`,
    columns: { product: "Product", return: "Return", risk: "Risk", source: "Where the return comes from" },
  },
};

const BADGE_STYLE = {
  return: {
    low: "border-rose-300/50 bg-rose-50/80 dark:border-rose-600/40 dark:bg-rose-900/30",
    medium: "border-amber-300/50 bg-amber-50/80 dark:border-amber-600/40 dark:bg-amber-900/30",
    high: "border-emerald-300/50 bg-emerald-50/80 dark:border-emerald-600/40 dark:bg-emerald-900/30",
  },
  risk: {
    low: "border-emerald-300/50 bg-emerald-50/80 dark:border-emerald-600/40 dark:bg-emerald-900/30",
    medium: "border-amber-300/50 bg-amber-50/80 dark:border-amber-600/40 dark:bg-amber-900/30",
    high: "border-rose-300/50 bg-rose-50/80 dark:border-rose-600/40 dark:bg-rose-900/30",
  },
} satisfies Record<"risk" | "return", Record<Level, string>>;

// 色だけを持ち、凡例とツールチップのラベルは locale 辞書から差し込む。
const LINE_COLOR = {
  deposit: "#16a34a",
  bond: "#2563eb",
  reit: "#9333ea",
  equity: "#e11d48",
  gold: "#f59e0b",
  commodity: "#0f766e",
  bitcoin: "#64748b",
} satisfies Record<LineKey, string>;

function buildChartConfig(locale: Locale): ChartConfig {
  const t = STRINGS[locale];
  return Object.fromEntries(
    lineKeys.map((key) => [key, { label: t.product[key], color: LINE_COLOR[key] }])
  ) as ChartConfig;
}

const indexedPerformanceData: IndexedPerformanceRow[] = [
  { month: "2021-04", deposit: 100, bond: 100, reit: 100, equity: 100, gold: 100, commodity: 100, bitcoin: 100 },
  { month: "2021-05", deposit: 100, bond: 99.9, reit: 103.5, equity: 102.1, gold: 93, commodity: 97.2, bitcoin: 100 },
  {
    month: "2021-06",
    deposit: 100.1,
    bond: 100.8,
    reit: 106.1,
    equity: 101.4,
    gold: 95.5,
    commodity: 102.1,
    bitcoin: 95.3,
  },
  {
    month: "2021-07",
    deposit: 100.1,
    bond: 101,
    reit: 108.7,
    equity: 104.6,
    gold: 95.2,
    commodity: 97.8,
    bitcoin: 111.7,
  },
  {
    month: "2021-08",
    deposit: 100.1,
    bond: 99.1,
    reit: 105.7,
    equity: 103.6,
    gold: 92.7,
    commodity: 107.6,
    bitcoin: 127,
  },
  {
    month: "2021-09",
    deposit: 100.2,
    bond: 101.9,
    reit: 114.5,
    equity: 108.7,
    gold: 96.6,
    commodity: 114.2,
    bitcoin: 119.4,
  },
  {
    month: "2021-10",
    deposit: 100.2,
    bond: 101,
    reit: 114.1,
    equity: 106.6,
    gold: 97.1,
    commodity: 110.1,
    bitcoin: 171.4,
  },
  {
    month: "2021-11",
    deposit: 100.2,
    bond: 102.1,
    reit: 121,
    equity: 111.2,
    gold: 99,
    commodity: 106.8,
    bitcoin: 157.9,
  },
  {
    month: "2021-12",
    deposit: 100.3,
    bond: 99.9,
    reit: 114.4,
    equity: 106.4,
    gold: 98.7,
    commodity: 122.1,
    bitcoin: 130.4,
  },
  {
    month: "2022-01",
    deposit: 100.3,
    bond: 98.6,
    reit: 111.7,
    equity: 104.2,
    gold: 105,
    commodity: 126.2,
    bitcoin: 108.4,
  },
  {
    month: "2022-02",
    deposit: 100.4,
    bond: 99.9,
    reit: 125.6,
    equity: 113.5,
    gold: 111.9,
    commodity: 151.2,
    bitcoin: 121.6,
  },
  {
    month: "2022-03",
    deposit: 100.4,
    bond: 102.4,
    reit: 130.5,
    equity: 112,
    gold: 116.3,
    commodity: 165.3,
    bitcoin: 135.6,
  },
  {
    month: "2022-04",
    deposit: 100.4,
    bond: 100.5,
    reit: 119.3,
    equity: 109.1,
    gold: 113.4,
    commodity: 165.9,
    bitcoin: 119.8,
  },
  {
    month: "2022-05",
    deposit: 100.5,
    bond: 103.1,
    reit: 115.3,
    equity: 105.6,
    gold: 117.9,
    commodity: 160.9,
    bitcoin: 100.1,
  },
  {
    month: "2022-06",
    deposit: 100.5,
    bond: 103.1,
    reit: 122.1,
    equity: 110,
    gold: 111.8,
    commodity: 152.6,
    bitcoin: 65.7,
  },
  {
    month: "2022-07",
    deposit: 100.5,
    bond: 102.9,
    reit: 121.2,
    equity: 112.2,
    gold: 113.7,
    commodity: 164.2,
    bitcoin: 76.1,
  },
  {
    month: "2022-08",
    deposit: 100.6,
    bond: 100,
    reit: 106,
    equity: 107.2,
    gold: 114.7,
    commodity: 155.9,
    bitcoin: 68.3,
  },
  {
    month: "2022-09",
    deposit: 100.6,
    bond: 104,
    reit: 115.7,
    equity: 113.6,
    gold: 115.9,
    commodity: 161.6,
    bitcoin: 68.8,
  },
  { month: "2022-10", deposit: 100.6, bond: 101.9, reit: 114, equity: 113, gold: 116, commodity: 154.7, bitcoin: 74.6 },
  {
    month: "2022-11",
    deposit: 100.7,
    bond: 97.2,
    reit: 105.8,
    equity: 107.2,
    gold: 115.3,
    commodity: 146,
    bitcoin: 57.7,
  },
  {
    month: "2022-12",
    deposit: 100.7,
    bond: 97.6,
    reit: 113.4,
    equity: 109.9,
    gold: 118.7,
    commodity: 138.3,
    bitcoin: 53.1,
  },
  {
    month: "2023-01",
    deposit: 100.7,
    bond: 99.1,
    reit: 114.7,
    equity: 113,
    gold: 117.8,
    commodity: 142.4,
    bitcoin: 73.7,
  },
  {
    month: "2023-02",
    deposit: 100.8,
    bond: 98.2,
    reit: 105.6,
    equity: 112.2,
    gold: 125.5,
    commodity: 133.5,
    bitcoin: 77.2,
  },
  {
    month: "2023-03",
    deposit: 100.8,
    bond: 101.5,
    reit: 110.3,
    equity: 115.8,
    gold: 127.5,
    commodity: 135.2,
    bitcoin: 92.5,
  },
  {
    month: "2023-04",
    deposit: 100.9,
    bond: 103.6,
    reit: 110.4,
    equity: 119.8,
    gold: 130.4,
    commodity: 134.8,
    bitcoin: 97.7,
  },
  {
    month: "2023-05",
    deposit: 100.9,
    bond: 107.6,
    reit: 117.6,
    equity: 128.8,
    gold: 131.3,
    commodity: 143.1,
    bitcoin: 92.6,
  },
  {
    month: "2023-06",
    deposit: 100.9,
    bond: 106,
    reit: 120.4,
    equity: 133,
    gold: 131.6,
    commodity: 149.4,
    bitcoin: 107.6,
  },
  {
    month: "2023-07",
    deposit: 101,
    bond: 107.8,
    reit: 121.9,
    equity: 133.4,
    gold: 134.7,
    commodity: 153.1,
    bitcoin: 101.8,
  },
  {
    month: "2023-08",
    deposit: 101,
    bond: 105.2,
    reit: 113.9,
    equity: 131.6,
    gold: 132.4,
    commodity: 158.4,
    bitcoin: 92.3,
  },
  {
    month: "2023-09",
    deposit: 101,
    bond: 107.1,
    reit: 108.6,
    equity: 126.8,
    gold: 142,
    commodity: 160.8,
    bitcoin: 98.5,
  },
  {
    month: "2023-10",
    deposit: 101.1,
    bond: 110.4,
    reit: 119.2,
    equity: 136.2,
    gold: 142.4,
    commodity: 155.1,
    bitcoin: 128.3,
  },
  {
    month: "2023-11",
    deposit: 101.1,
    bond: 110,
    reit: 127.2,
    equity: 138,
    gold: 139.4,
    commodity: 146.3,
    bitcoin: 136.5,
  },
  {
    month: "2023-12",
    deposit: 101.1,
    bond: 112.6,
    reit: 128.5,
    equity: 145,
    gold: 142.5,
    commodity: 147.7,
    bitcoin: 145.8,
  },
  {
    month: "2024-01",
    deposit: 101.2,
    bond: 113.2,
    reit: 129.1,
    equity: 152,
    gold: 145,
    commodity: 146.8,
    bitcoin: 153.1,
  },
  {
    month: "2024-02",
    deposit: 101.2,
    bond: 113.7,
    reit: 133.5,
    equity: 158.6,
    gold: 166.3,
    commodity: 152.9,
    bitcoin: 224.7,
  },
  {
    month: "2024-03",
    deposit: 101.2,
    bond: 117.6,
    reit: 132.1,
    equity: 160.5,
    gold: 173.1,
    commodity: 169.4,
    bitcoin: 264.2,
  },
  {
    month: "2024-04",
    deposit: 101.3,
    bond: 118.3,
    reit: 134.6,
    equity: 164.7,
    gold: 174.8,
    commodity: 167.4,
    bitcoin: 234,
  },
  {
    month: "2024-05",
    deposit: 101.3,
    bond: 122.3,
    reit: 140.5,
    equity: 172.8,
    gold: 176.9,
    commodity: 168.3,
    bitcoin: 259.6,
  },
  {
    month: "2024-06",
    deposit: 101.4,
    bond: 118.5,
    reit: 144.1,
    equity: 166.6,
    gold: 174.9,
    commodity: 153,
    bitcoin: 246.8,
  },
  { month: "2024-07", deposit: 101.4, bond: 115, reit: 144, equity: 162, gold: 172, commodity: 146.8, bitcoin: 236.8 },
  {
    month: "2024-08",
    deposit: 101.4,
    bond: 112.6,
    reit: 143.9,
    equity: 163,
    gold: 177.9,
    commodity: 149.5,
    bitcoin: 210.9,
  },
  {
    month: "2024-09",
    deposit: 101.5,
    bond: 119.4,
    reit: 153.9,
    equity: 173.1,
    gold: 201.5,
    commodity: 160.9,
    bitcoin: 222.7,
  },
  {
    month: "2024-10",
    deposit: 101.5,
    bond: 117,
    reit: 153.3,
    equity: 174.4,
    gold: 188.5,
    commodity: 160.1,
    bitcoin: 261,
  },
  {
    month: "2024-11",
    deposit: 101.5,
    bond: 120.9,
    reit: 147.3,
    equity: 180.3,
    gold: 194.9,
    commodity: 165.6,
    bitcoin: 353.4,
  },
  {
    month: "2024-12",
    deposit: 101.6,
    bond: 119.1,
    reit: 147.4,
    equity: 183.4,
    gold: 203.7,
    commodity: 165.8,
    bitcoin: 359.7,
  },
  {
    month: "2025-01",
    deposit: 101.6,
    bond: 116.8,
    reit: 144.8,
    equity: 173.8,
    gold: 202.7,
    commodity: 168.3,
    bitcoin: 388.8,
  },
  {
    month: "2025-02",
    deposit: 101.6,
    bond: 116.2,
    reit: 138.9,
    equity: 166,
    gold: 222.5,
    commodity: 172.6,
    bitcoin: 310.9,
  },
  {
    month: "2025-03",
    deposit: 101.7,
    bond: 115.3,
    reit: 133.4,
    equity: 161.4,
    gold: 222.2,
    commodity: 159,
    bitcoin: 302.8,
  },
  {
    month: "2025-04",
    deposit: 101.7,
    bond: 116.1,
    reit: 139.2,
    equity: 172.4,
    gold: 223.6,
    commodity: 152.5,
    bitcoin: 329.6,
  },
  {
    month: "2025-05",
    deposit: 101.7,
    bond: 118.5,
    reit: 138.3,
    equity: 178.3,
    gold: 223.6,
    commodity: 165.3,
    bitcoin: 368.7,
  },
  {
    month: "2025-06",
    deposit: 101.8,
    bond: 121,
    reit: 145.1,
    equity: 190.7,
    gold: 231.7,
    commodity: 163,
    bitcoin: 376.9,
  },
  {
    month: "2025-07",
    deposit: 101.8,
    bond: 122.7,
    reit: 146.6,
    equity: 191.8,
    gold: 236.4,
    commodity: 163.4,
    bitcoin: 427.1,
  },
  {
    month: "2025-08",
    deposit: 101.9,
    bond: 121,
    reit: 147.2,
    equity: 199.4,
    gold: 296.4,
    commodity: 168.4,
    bitcoin: 389.6,
  },
  {
    month: "2025-09",
    deposit: 101.9,
    bond: 128.2,
    reit: 152.8,
    equity: 212.7,
    gold: 299.1,
    commodity: 177.7,
    bitcoin: 413,
  },
  {
    month: "2025-10",
    deposit: 101.9,
    bond: 132.2,
    reit: 156.9,
    equity: 214.9,
    gold: 313,
    commodity: 182.9,
    bitcoin: 412.7,
  },
  {
    month: "2025-11",
    deposit: 102,
    bond: 131.1,
    reit: 155.2,
    equity: 217.7,
    gold: 324.6,
    commodity: 183.1,
    bitcoin: 344.8,
  },
  {
    month: "2025-12",
    deposit: 102,
    bond: 130.1,
    reit: 156.9,
    equity: 220.5,
    gold: 385.3,
    commodity: 208.8,
    bitcoin: 335.8,
  },
  {
    month: "2026-01",
    deposit: 102,
    bond: 133.4,
    reit: 169.7,
    equity: 226.1,
    gold: 383.9,
    commodity: 205.5,
    bitcoin: 297.6,
  },
  {
    month: "2026-02",
    deposit: 102.1,
    bond: 130.1,
    reit: 159.1,
    equity: 211.5,
    gold: 341.7,
    commodity: 247.1,
    bitcoin: 255.8,
  },
  {
    month: "2026-03",
    deposit: 102.1,
    bond: 132.8,
    reit: 164.3,
    equity: 218.6,
    gold: 348.7,
    commodity: 246.5,
    bitcoin: 264.7,
  },
];

const lineKeys = [
  "deposit",
  "bond",
  "reit",
  "equity",
  "gold",
  "commodity",
  "bitcoin",
] as const satisfies readonly LineKey[];

function calcAnnualizedReturn(values: number[]) {
  if (values.length < 2) return 0;

  const periods = values.length - 1;
  const years = periods / 12;
  const start = values[0];
  const end = values[values.length - 1];

  return (Math.pow(end / start, 1 / years) - 1) * 100;
}

function calcAnnualizedVolatility(values: number[]) {
  if (values.length < 3) return 0;

  const returns = values.slice(1).map((value, index) => value / values[index] - 1);
  const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length;
  const variance = returns.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (returns.length - 1);

  return Math.sqrt(variance) * Math.sqrt(12) * 100;
}

function calcMaxDrawdown(values: number[]) {
  if (values.length === 0) return 0;

  let peak = values[0];
  let maxDrawdown = 0;

  values.forEach((value) => {
    peak = Math.max(peak, value);
    maxDrawdown = Math.max(maxDrawdown, (peak - value) / peak);
  });

  return maxDrawdown * 100;
}

function classifyReturnLevel(annualizedReturn: number): Level {
  if (annualizedReturn < 7) return "low";
  if (annualizedReturn < 20) return "medium";
  return "high";
}

function classifyRiskLevel(annualizedVolatility: number, maxDrawdown: number): Level {
  if (annualizedVolatility >= 20 || maxDrawdown >= 35) return "high";
  if (annualizedVolatility >= 10 || maxDrawdown >= 15) return "medium";
  return "low";
}

function LevelBadge({ kind, level }: { kind: "risk" | "return"; level: Level }) {
  const t = STRINGS[useDocLocale()];
  return (
    <Badge variant="outline" className={BADGE_STYLE[kind][level]}>
      {t.level[level]}
    </Badge>
  );
}

const indexedPerformanceMetrics = lineKeys.map((key) => {
  const values = indexedPerformanceData.map((row) => row[key]);
  const annualizedReturn = calcAnnualizedReturn(values);
  const annualizedVolatility = calcAnnualizedVolatility(values);
  const maxDrawdown = calcMaxDrawdown(values);

  return {
    key,
    annualizedReturn,
    annualizedVolatility,
    maxDrawdown,
    returnLevel: classifyReturnLevel(annualizedReturn),
    riskLevel: classifyRiskLevel(annualizedVolatility, maxDrawdown),
  };
});

export function DocIndexedPerformanceChart() {
  const isMobile = useIsMobile();
  const locale = useDocLocale();
  const t = STRINGS[locale];
  const indexedPerformanceConfig = buildChartConfig(locale);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.chartTitle}</CardTitle>
        <CardDescription>{t.chartDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[480px]">
          <ChartContainer config={indexedPerformanceConfig} className="h-full w-full">
            <LineChart
              data={indexedPerformanceData}
              margin={{ top: 20, right: isMobile ? 12 : 100, bottom: 10, left: 4 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="5" stroke="var(--border)" />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tickMargin={-10}
                interval={3}
                fontSize={10}
                angle={-45}
                textAnchor="end"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={36}
                domain={[0, 450]}
                ticks={[0, 50, 100, 150, 200, 250, 300, 350, 400, 450]}
                fontSize={11}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    indicator="line"
                    labelFormatter={(label) => {
                      const labelText = typeof label === "string" ? label : String(label ?? "");
                      const [y, m] = labelText.split("-");
                      if (!y || !m) return labelText;
                      return t.monthLabel(y, Number(m));
                    }}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              {lineKeys.map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={`var(--color-${key})`}
                  strokeWidth={key === "equity" ? 2.5 : 2}
                  dot={false}
                  activeDot={{ r: 4 }}
                >
                  {!isMobile && (
                    <LabelList
                      dataKey={key}
                      position="right"
                      offset={8}
                      fontSize={11}
                      fill={LINE_COLOR[key]}
                      formatter={() => t.product[key]}
                      content={({ x, y, index }) => {
                        if (index !== indexedPerformanceData.length - 1) return null;
                        return (
                          <text
                            x={Number(x) + 8}
                            y={Number(y)}
                            fill={LINE_COLOR[key]}
                            fontSize={11}
                            fontWeight={500}
                            dominantBaseline="central"
                          >
                            {t.product[key]}
                          </text>
                        );
                      }}
                    />
                  )}
                </Line>
              ))}
            </LineChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export function DocAssetClassRiskReturnTable() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="my-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.columns.product}</TableHead>
            <TableHead>{t.columns.return}</TableHead>
            <TableHead>{t.columns.risk}</TableHead>
            <TableHead>{t.columns.source}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {indexedPerformanceMetrics.map((item) => (
            <TableRow key={item.key}>
              <TableCell className="align-top text-sm">
                <span className="whitespace-normal leading-4">{t.product[item.key]}</span>
              </TableCell>
              <TableCell className="align-top text-sm">
                <LevelBadge kind="return" level={item.returnLevel} />
              </TableCell>
              <TableCell className="align-top text-sm">
                <LevelBadge kind="risk" level={item.riskLevel} />
              </TableCell>
              <TableCell className="align-top text-sm">
                <span className="whitespace-normal leading-4">{t.source[item.key]}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
